import { useParams } from "react-router"
import { useRef, useState } from "react"
import * as Icons from "@nova/component-icons"
import { Dropdown } from "@nova/component-dropdown"
import { useBatchTagMissions, useCreateTag, useTags } from "features/pilot-tracking/tags/TagQueries"
import { Card } from "../../MetricLayout"
import { TagsQueryResult } from "../MissionPageQueries"
import { Tag } from "@nova/ds-component-tag"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { MAX_MISSION_TAG_LENGTH_CHARS } from "@nova/constants"

export const TagsCard = ({ tags }: { tags: TagsQueryResult }) => {
    const { missionId = "" } = useParams()
    const { isEditor } = useWorkspacePermissions()
    const [inputValue, setInputValue] = useState("")
    const inputRef = useRef<HTMLInputElement>(null)

    const { data: allTags } = useTags()
    const { batchTagMissions } = useBatchTagMissions()
    const { createTag } = useCreateTag()

    const assignedTagIds = new Set(tags.map((t) => t.id))
    const filteredTags = (allTags ?? []).filter(
        (t) =>
            !assignedTagIds.has(t.id) &&
            t.name.toLowerCase().includes(inputValue.trim().toLowerCase())
    )
    const showCreateOption =
        inputValue.trim().length > 0 &&
        !(allTags ?? []).some((t) => t.name.toLowerCase() === inputValue.trim().toLowerCase())

    const handleAddTag = async (tagId: string) => {
        await batchTagMissions({ additions: [{ tagId, missionId }], deletions: [] })
        setInputValue("")
        inputRef.current?.focus()
    }

    const isCreating = useRef(false)
    const handleCreateAndAddTag = async () => {
        if (isCreating.current) return
        isCreating.current = true
        try {
            const newTag = await createTag({ name: inputValue.trim() })
            if (newTag) await handleAddTag(newTag.id)
        } finally {
            isCreating.current = false
        }
    }

    const handleRemoveTag = async (tagId: string) => {
        await batchTagMissions({ additions: [], deletions: [{ tagId, missionId }] })
    }

    return (
        <Card className="flex flex-col gap-3 flex-1 !px-3">
            <h2 className="text-white px-2 telegraf">Tags</h2>
            {isEditor ? (
                <Dropdown>
                    <Dropdown.Trigger
                        as="div"
                        clickBehaviour="open"
                        className="flex flex-wrap gap-1 rounded-md min-h-9 items-center px-2 py-1.5 border border-transparent hover:bg-gray-700 cursor-text transition-colors"
                    >
                        {tags.map((tag) => (
                            <Tag
                                key={tag.id}
                                onRemove={(e) => {
                                    e.stopPropagation()
                                    void handleRemoveTag(tag.id)
                                }}
                            >
                                <span className="text-sm">{tag.name}</span>
                            </Tag>
                        ))}
                        <input
                            ref={inputRef}
                            placeholder={tags.length === 0 ? "Add tags..." : undefined}
                            className="bg-transparent outline-none border-none text-white text-sm placeholder-gray-500 flex-1 min-w-20 focus:ring-0"
                            value={inputValue}
                            onChange={(e) =>
                                setInputValue(
                                    e.currentTarget.value.slice(0, MAX_MISSION_TAG_LENGTH_CHARS)
                                )
                            }
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault()
                                    if (filteredTags.length > 0)
                                        void handleAddTag(filteredTags[0].id)
                                    else if (showCreateOption) void handleCreateAndAddTag()
                                }
                            }}
                        />
                    </Dropdown.Trigger>
                    {(filteredTags.length > 0 || showCreateOption) && (
                        <Dropdown.Menu className="min-w-[300px]">
                            <ScrollableArea className="max-h-48">
                                {filteredTags.map((tag) => (
                                    <Dropdown.ButtonItem
                                        key={tag.id}
                                        clickBehaviour="keep-open"
                                        className="text-sm"
                                        onClick={() => void handleAddTag(tag.id)}
                                    >
                                        {tag.name}
                                    </Dropdown.ButtonItem>
                                ))}
                            </ScrollableArea>
                            {showCreateOption && (
                                <Dropdown.ButtonItem
                                    clickBehaviour="keep-open"
                                    className="gap-1.5 text-sm"
                                    onClick={() => void handleCreateAndAddTag()}
                                >
                                    <Icons.SmallIcon Icon={Icons.Plus} />
                                    <span>Create &ldquo;{inputValue.trim()}&rdquo;</span>
                                </Dropdown.ButtonItem>
                            )}
                        </Dropdown.Menu>
                    )}
                </Dropdown>
            ) : (
                <div className="flex flex-wrap gap-1 min-h-9 items-center px-2 py-1.5">
                    {tags.map((tag) => (
                        <Tag key={tag.id}>
                            <span className="text-sm">{tag.name}</span>
                        </Tag>
                    ))}
                </div>
            )}
        </Card>
    )
}
